import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import { AMBIENT_SCRIBE_TRIAL_WITHOUT_CARD_FEATURE } from "constants/feature-flags";
import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import { setFeatures } from "features/auth/auth-slice";
import { useMedicalScribe } from "features/medical-scribe/use-medical-scribe.hook";
import {
  useGetFeatureSubscriptionsQuery,
  useGetPaywallFeatureByKeyQuery,
  useLazyGetCurrentUserPaywallFeaturesQuery
} from "services/paywall";

import {
  clearScribePurchaseMarker,
  isPurchasedRowLive,
  useScribePurchaseMarker,
  writeScribePurchaseMarker
} from "../hooks/use-scribe-purchase-marker.hook";
import { SCRIBE_PURCHASE_RETURN_COPY as COPY } from "./ambient-scribe-purchase-placeholder/ambient-scribe-purchase-placeholder.copy";

export const PURCHASE_POLL_MS = 3000;
/** Ten polls, so about 30 seconds, before the user is told to check back. */
export const PURCHASE_POLL_TRIES = 10;
/** After that, the wait goes on more slowly until the marker is stale (15 minutes). */
export const PURCHASE_SLOW_POLL_MS = 30000;

const STRIPE_RETURN_PARAMS = ["status", "feature_id", "subscription_id"];

interface StripeSuccess {
  featureId: number;
  subscriptionId: number;
}

/** Checkout's success URL: `…/?status=success&feature_id=<id>&subscription_id=<id>`. */
function readStripeSuccess(search: string): StripeSuccess | null {
  const params = new URLSearchParams(search);
  if (params.get("status") !== "success") return null;
  const featureId = Number(params.get("feature_id"));
  const subscriptionId = Number(params.get("subscription_id"));
  const isId = (id: number) => Number.isSafeInteger(id) && id > 0;
  return isId(featureId) && isId(subscriptionId)
    ? { featureId, subscriptionId }
    : null;
}

/**
 * Stripe sends the buyer back before its webhook has necessarily made the purchase live (SN-19868).
 * Mounted once in `Layout`, because the return can land on any route the purchase started from,
 * and the purchase screen may have been inside the recording widget's dialog, which is closed by
 * then. Renders nothing but snackbars.
 *
 * It polls the purchased ROW, not the buyer's own `/me/features/`: a practice that buys for the
 * colleagues who tried Scribe may give the buyer no licence at all, so the buyer's own access is
 * not a sign the purchase finished.
 */
export default function ScribePurchaseReturn() {
  const flags = useFlags();
  const isFlagOn = flags[AMBIENT_SCRIBE_TRIAL_WITHOUT_CARD_FEATURE] === true;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { setIsAmbientScribeFeatureAvailable } = useMedicalScribe();
  const [fetchUserFeatures] = useLazyGetCurrentUserPaywallFeaturesQuery();

  // Kept the moment it is seen. Whether it is Scribe's waits on a lookup of Scribe's feature id
  // (other purchases return to the same params), and a click in that window could take the
  // params off the URL before the marker is written.
  const seenSuccess = isFlagOn ? readStripeSuccess(location.search) : null;
  const seenFeatureId = seenSuccess?.featureId;
  const seenSubscriptionId = seenSuccess?.subscriptionId;
  const [stripeReturn, setStripeReturn] = useState<StripeSuccess | null>(null);
  useEffect(() => {
    if (seenFeatureId && seenSubscriptionId) {
      setStripeReturn({
        featureId: seenFeatureId,
        subscriptionId: seenSubscriptionId
      });
    }
  }, [seenFeatureId, seenSubscriptionId]);

  const { data: scribeFeature } = useGetPaywallFeatureByKeyQuery(
    AMBIENT_SCRIBE_FEATURE_KEY,
    { skip: !stripeReturn }
  );

  useEffect(() => {
    if (!stripeReturn || !scribeFeature) return;
    setStripeReturn(null);
    if (stripeReturn.featureId !== scribeFeature.id) return;
    writeScribePurchaseMarker({
      subscriptionId: stripeReturn.subscriptionId,
      at: Date.now()
    });
    // Only if they are still there. Replaced, so Back does not land on the return again and
    // re-arm the wait.
    if (
      readStripeSuccess(location.search)?.subscriptionId !==
      stripeReturn.subscriptionId
    ) {
      return;
    }
    const params = new URLSearchParams(location.search);
    STRIPE_RETURN_PARAMS.forEach((key) => params.delete(key));
    const search = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : "",
        hash: location.hash
      },
      { replace: true }
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    navigate,
    scribeFeature,
    stripeReturn
  ]);

  const marker = useScribePurchaseMarker();
  const pendingId = isFlagOn ? (marker?.subscriptionId ?? null) : null;
  // Past the first half-minute the user has been told to check back, and the wait slows down.
  const [slowFor, setSlowFor] = useState<number | null>(null);
  const isSlow = pendingId !== null && slowFor === pendingId;
  // The same `limit: 100` as the purchase screen's own read, so polling here refreshes it too.
  const { data: subscriptions } = useGetFeatureSubscriptionsQuery(
    { limit: 100 },
    {
      skip: pendingId === null,
      pollingInterval: isSlow ? PURCHASE_SLOW_POLL_MS : PURCHASE_POLL_MS
    }
  );
  const isLive =
    pendingId !== null && isPurchasedRowLive(subscriptions, pendingId);

  const finishedRef = useRef<number | null>(null);
  useEffect(() => {
    if (pendingId === null || !isLive || finishedRef.current === pendingId) {
      return;
    }
    finishedRef.current = pendingId;
    clearScribePurchaseMarker();
    // `auth.features` is otherwise read once at login. Check `isSuccess`, not `"data" in`: a failed
    // refetch still carries the previous `data`, because RTK keeps it on rejection.
    fetchUserFeatures().then((features) => {
      if (features.isSuccess && features.data) {
        dispatch(setFeatures(features.data));
        // Only a licence of the buyer's own turns Scribe on here: they may have bought for others.
        if (features.data[AMBIENT_SCRIBE_FEATURE_KEY]?.active) {
          setIsAmbientScribeFeatureAvailable(true);
        }
      }
      dispatch(setCustomMessage(COPY.complete));
      dispatch(setOpen(true));
    });
  }, [
    dispatch,
    fetchUserFeatures,
    isLive,
    pendingId,
    setIsAmbientScribeFeatureAvailable
  ]);

  /**
   * A slow webhook is not a failed one, so the marker stays: Stripe retries a webhook for days,
   * and while the purchased row is still pending, checkout would take a second payment for it.
   * The wait ends when the row goes live or the marker goes stale.
   */
  useEffect(() => {
    if (pendingId === null) return undefined;
    const timer = setTimeout(() => {
      setSlowFor(pendingId);
      dispatch(setCustomMessage(COPY.slow));
      dispatch(setOpen(true));
    }, PURCHASE_POLL_MS * PURCHASE_POLL_TRIES);
    return () => clearTimeout(timer);
  }, [dispatch, pendingId]);

  return null;
}
